<template>
  <div
    class="comment"
    @mouseover="showPreview()"
    @mouseout="hidePreview()"
    :class="{ 'input--error': commentHasError }"
    ref="commentEl"
  >
    <template v-if="!readonly">
      <textarea v-model="commentToEdit"> </textarea>
      <div
        class="comment__submit"
        v-if="
          commentToEdit !== comment && (commentToEdit.trim('') || !isRequired)
        "
        @click="update()"
      ></div>
    </template>

    <template v-else>
      <textarea v-model="commentToEdit" readonly> </textarea>
    </template>

    <div
      class="comment__preview"
      ref="commentPreview"
      :style="{
        left: `${previewXPos}px`,
        top: `${previewYPos}px`,
        width: `${previewWidth}px`,
        opacity: show ? 1 : 0,
      }"
    >
      <div class="comment__preview-text">
        {{ commentToEdit }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  isRequired: {
    required: false,
    type: Boolean,
    default: false,
  },
  comment: {
    required: false,
    type: String,
  },
  updateComment: {
    required: false,
    type: Function,
  },
  readonly: {
    required: false,
    type: Boolean,
    default: false,
  },
});

const commentToEdit = ref("");
const commentHasError = ref(false);
const previewXPos = ref(0);
const previewYPos = ref(0);
const previewWidth = ref(0);
const show = ref(false);

// Template refs
const commentEl = ref(null);
const commentPreview = ref(null);

const update = () => {
  if (props.isRequired) {
    if (commentToEdit.value.trim("")) {
      props.updateComment(commentToEdit.value);
    } else {
      commentHasError.value = true;
    }
  } else {
    props.updateComment(commentToEdit.value);
  }
};

const showPreview = () => {
  calculatePreviewWidthAndPos();
  show.value = true;
};

const hidePreview = () => {
  show.value = false;
  previewXPos.value = 0;
  previewYPos.value = 0;
};

const deletePreview = () => {
  document.body.removeChild(commentPreview.value);
};

const calculatePreviewWidthAndPos = () => {
  const rect = commentEl.value.getBoundingClientRect();
  previewXPos.value = rect.x;
  previewYPos.value = rect.y + 55;
};

// Watch for comment prop changes
watch(
  () => props.comment,
  (value) => {
    commentToEdit.value = value;
  }
);

// Conditional watch for isRequired validation
if (props.isRequired) {
  watch(commentToEdit, (value) => {
    if (value.trim("")) {
      commentHasError.value = false;
    } else {
      commentHasError.value = true;
    }
  });
}

onMounted(() => {
  document.body.append(commentPreview.value);
  previewWidth.value = commentEl.value.clientWidth;
  commentToEdit.value = props.comment;
});

onBeforeUnmount(() => {
  if (document.body.contains(commentPreview.value)) {
    deletePreview();
  }
});
</script>

<style scoped lang="scss"> 
.comment {
  background: var(--bg-15);
  width: 100%;
  min-height: 50px;
  max-height: 50px;
  border-radius: var(--radius-1);
  display: flex;
  align-items: center;
  padding: 2px 10px;
  position: relative;

  textarea {
    width: 100%;
    background: 0;
    border: 0;
    resize: none;
    color: var(--text-9);
  }

  &__submit {
    width: 24px;
    height: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-left: 5px;
    background: url("../assets/icons/default/confirm_icon.svg");

    &:hover {
      background: url("../assets/icons/default/confirm_hover_icon.svg");
    }
  }

  &__preview {
    position: absolute;
    background: var(--bg-1);
    border-radius: var(--radius-1);
    padding: 10px;
    z-index: 99;
    white-space: pre-wrap;
    pointer-events: none;

    &-text {
      word-break: break-all;
    }
  }
}

[data-theme="dark"] .comment__preview{ 
  border: 1px solid #313b47;
  background-color: #46505b;
  color: white !important;
}
</style>
